/* eslint-disable react/prop-types */
import React, { useState, FC, HTMLAttributes } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-scroll'
import { useRecoilValue } from 'recoil'
import { Text, IconButton, Box, Flex, Heading, Skeleton } from '@chakra-ui/core'
import { QuantityBox } from '@engyalo/storefront-components'
import {
  IProductPricedPerCollectionLevel1,
  IProductsPricedPerCollectionCollection,
} from '@engyalo/storefront-types/lib/graphql/product'
import { ProductList, ProductListFrom } from '../../components/ProductList'
import { Searchbar } from '../../components/Searchbar'
import { CartIcon } from '../../components/CartIcon'
import { MinAmountProgress } from '../../components/MinAmountProgress'
import { cartStatsState } from '../../recoil/selectors'
import { configState } from '../../recoil/atoms'
import { IConfig } from '../../recoil/types'
import { BoxLinkButton } from '../../lib/types'
import { notEmpty } from '../../lib/utilities'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  loading: boolean
  products: (IProductsPricedPerCollectionCollection | null)[]
}

export const Catalogue: FC<IProps> = ({ loading, products }: IProps) => {
  const { t } = useTranslation()
  const { customer, order } = useParams()
  const [searchbarStatus, setSearchbarStatus] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const { totalNum, totalPrice } = useRecoilValue(cartStatsState)
  const config = useRecoilValue<IConfig>(configState)

  const handleSetActive = (section: string): void => {
    setActiveSection(section)
    const activeSectionRef = document.getElementById(`${section}-link`)
    if (activeSectionRef) {
      activeSectionRef.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
    }
  }

  const handleSetInactive = (section: string): void => {
    if (activeSection === section) setActiveSection('')
  }

  return (
    <Flex h="100%" w="100%" flexDirection="column">
      {searchbarStatus && <Searchbar status={searchbarStatus} closeAction={(): void => setSearchbarStatus(false)} />}
      {!searchbarStatus && (
        <Flex h="40px" w="100%" justifyContent="space-between" padding="8px 8px 0 8px">
          <img alt="logo" height="100%" src={config.logo} />

          <Flex>
            <IconButton
              h="100%"
              w="32px"
              background="transparent"
              aria-label="search"
              size="lg"
              icon="search"
              onClick={(): void => setSearchbarStatus(true)}
              borderStyle="none"
              borderColor="unset"
            />
            <CartIcon />
          </Flex>
        </Flex>
      )}
      {searchbarStatus && (
        <Box
          position="absolute"
          opacity={0.5}
          backgroundColor="black"
          width={['100%', '425px']}
          height={['100%', '720px']}
          zIndex={50}
        />
      )}
      <Flex width="100%" overflowX="auto" aria-hidden id="navbar">
        {loading
          ? [1, 2, 3, 4].map((number) => <Skeleton key={number} marginX="8px" height="16px" width="108px" />)
          : products.map((collection) => {
              const slug = collection?.name || ''
              if (collection) {
                return (
                  <Link
                    activeClass="active"
                    onSetActive={handleSetActive}
                    to={slug}
                    spy={true}
                    smooth={true}
                    duration={250}
                    containerId="containerElement"
                    onSetInactive={(): void => handleSetInactive(slug)}
                    id={`${slug}-link`}
                    key={`${slug}-link`}
                  >
                    <Box
                      paddingY="16px"
                      paddingX="40px"
                      fontSize="md"
                      borderBottom="2px solid"
                      borderColor={activeSection === slug ? 'primary.500' : 'transparent'}
                      cursor="pointer"
                      whiteSpace="nowrap"
                    >
                      {collection.name}
                    </Box>
                  </Link>
                )
              }

              return null
            })}
      </Flex>
      <Box flex="1" overflow="auto" marginTop="12px" id="containerElement">
        {loading
          ? [1, 2, 3].map((number) => (
              <Box key={number}>
                <Skeleton height="18px" width="60px" margin="12px" />

                <ProductList products={Array(5).fill({})} from={ProductListFrom.COLLECTIONS} isLoading={loading} />
              </Box>
            ))
          : products.map((collection) => {
              if (collection && collection.productsPriced && collection.name) {
                const productsPriced: IProductPricedPerCollectionLevel1[] = collection.productsPriced.filter(notEmpty)

                const showPrice = collection.enabledPrice || true

                return (
                  <div id={collection.name} key={collection.name}>
                    <Heading as="h3" fontSize="md" padding="8px 0 4px 12px" fontWeight="500">
                      {collection.name}
                    </Heading>
                    <ProductList products={productsPriced} from={ProductListFrom.COLLECTIONS} showPrice={showPrice} />
                  </div>
                )
              }

              return null
            })}
      </Box>
      {totalNum > 0 && (
        <Box zIndex={1} paddingY="12px" paddingX="8px" boxShadow="md" backgroundColor="white">
          {config.minAmount ? (
            <MinAmountProgress
              currencyPrefix={config.currencyPrefix}
              currentAmount={Number(totalPrice)}
              minAmount={config.minAmount}
            />
          ) : null}
          <BoxLinkButton
            to={`/${customer}/${order}/cart`}
            display="flex"
            width="100%"
            paddingY="12px"
            paddingX="16px"
            backgroundColor="primary.500"
            borderColor="transparent"
            fontWeight="500"
            color="white"
            fontFamily="body"
            fontSize="11px"
            borderRadius="4px"
            textDecor="none"
          >
            <QuantityBox size="big" value={totalNum} light />
            <Text flex="1" fontSize="lg" alignSelf="center" textAlign="center">
              {t('catalogue-goToCart')}
            </Text>
            <Text fontSize="lg" alignSelf="center">{`${config.currencyPrefix} ${totalPrice}`}</Text>
          </BoxLinkButton>
        </Box>
      )}
    </Flex>
  )
}
